import { type MutationOptions, type UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import {
  ApiError,
  getApiMe,
  getPutApiUserMutationOptions,
  type ModelsSmsMarketingConsentResponse,
  type ModelsUpdateSmsMarketingConsentRequest,
  putApiNotificationSettings,
  putApiNotificationSettingsSmsMarketingConsent,
  type UpdateNotificationSettingsRequest,
} from "twi-client";

import { queryClient } from "~/entry.client";
import { STORAGE_KEYS } from "~/lib/constants";
import { executeMutation } from "~/lib/react-query";
import { normalizeUser } from "~/lib/user.utils";
import type { UpdateMeOptions, User } from "~/types/user";

export const USER_KEY = {
  all: ["me"],
};

const userQueryFilters = {
  queryKey: USER_KEY.all,
  exact: true,
} as const;

type UpdateMeMutationOptions = {
  invalidateExactUserKey?: boolean;
};

function getApiErrorMessage(error: ApiError, fallback: string): string {
  if (typeof error.data === "string" && error.data.trim() !== "") {
    return error.data;
  }

  if (error.data && typeof error.data === "object" && "error" in error.data) {
    const message = error.data.error;

    if (typeof message === "string" && message.trim() !== "") {
      return message;
    }
  }

  return fallback;
}

const getMeOptions = {
  queryKey: USER_KEY.all,
  retry: false,
  staleTime: 1000 * 30, // Cache for 30 seconds to prevent redundant calls
  queryFn: async () => {
    try {
      const user = await getApiMe();
      if (!user) {
        return null;
      }

      return normalizeUser(user);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return null;
      }

      console.error("Network or other error fetching user:", error);
      return null;
    }
  },
} satisfies UseQueryOptions;

export const useUserQuery = () => {
  return useQuery(getMeOptions);
};

export async function ensureUserData() {
  let user: User | null = null;
  user = queryClient.getQueryData<User>(getMeOptions.queryKey) ?? null;
  if (!user) {
    user = (await queryClient.fetchQuery(getMeOptions)) ?? null;
  }
  if (!user) return null;

  // Compute hasDevoPlus
  const expiresAt = localStorage.getItem(STORAGE_KEYS.UPGRADE_SUCCESS);
  const hasDevoPlus = user.hasDevoPlus || Boolean(expiresAt && Date.now() < Number(expiresAt));

  // Return user with updated hasDevoPlus
  return {
    ...user,
    hasDevoPlus,
  };
}

export async function invalidateAndFetchUserData() {
  await queryClient.invalidateQueries({ queryKey: USER_KEY.all });
  return queryClient.fetchQuery(getMeOptions);
}

function createUpdateMeOptions(options?: UpdateMeMutationOptions) {
  return {
    mutationKey: USER_KEY.all,
    mutationFn: async (args: UpdateMeOptions) => {
      try {
        return await executeMutation(queryClient, getPutApiUserMutationOptions(), { data: args });
      } catch (error) {
        if (error instanceof ApiError) {
          console.error("Failed to update user", error.status, error.statusText);
          throw new Error(getApiErrorMessage(error, "Failed to update user"));
        }

        throw error;
      }
    },
    onMutate: async (args) => {
      await queryClient.cancelQueries(userQueryFilters);
      const prevMe = queryClient.getQueryData<User>(getMeOptions.queryKey) ?? null;
      queryClient.setQueryData<User>(getMeOptions.queryKey, (old) => {
        if (!old) {
          return old;
        }
        return { ...old, ...args };
      });
      return prevMe;
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(getMeOptions.queryKey, context);
      }
    },
    onSettled: async () => {
      if (options?.invalidateExactUserKey) {
        await queryClient.invalidateQueries(userQueryFilters);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: USER_KEY.all });
    },
  } satisfies MutationOptions<void, Error, UpdateMeOptions, User | null>;
}

export function useMeMutation(options?: UpdateMeMutationOptions) {
  return useMutation(createUpdateMeOptions(options));
}

export function useNotificationSettingsMutation(options?: UpdateMeMutationOptions) {
  return useMutation(createNotificationSettingsMutationOptions(options));
}

export function useSmsMarketingConsentMutation() {
  return useMutation(createSmsMarketingConsentMutationOptions());
}

function createNotificationSettingsMutationOptions(options?: UpdateMeMutationOptions) {
  return {
    mutationKey: ["notification-settings"],
    mutationFn: async (args: UpdateNotificationSettingsRequest) => {
      try {
        return await putApiNotificationSettings(args);
      } catch (error) {
        if (error instanceof ApiError) {
          console.error("Failed to update notification settings", error.status, error.statusText);
          throw new Error(getApiErrorMessage(error, "Failed to update notification settings"));
        }

        throw error;
      }
    },
    onSettled: async () => {
      if (options?.invalidateExactUserKey) {
        await queryClient.invalidateQueries(userQueryFilters);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: USER_KEY.all });
    },
  } satisfies MutationOptions<void, Error, UpdateNotificationSettingsRequest>;
}

async function refreshExactUserData() {
  await queryClient.invalidateQueries(userQueryFilters);
  return queryClient.fetchQuery(getMeOptions);
}

function createSmsMarketingConsentMutationOptions() {
  return {
    mutationKey: ["notification-settings", "sms-marketing-consent"],
    retry: false,
    mutationFn: async (args: ModelsUpdateSmsMarketingConsentRequest) => {
      try {
        return await putApiNotificationSettingsSmsMarketingConsent(args);
      } catch (error) {
        if (error instanceof ApiError) {
          console.error("Failed to update SMS marketing preference", error.status, error.statusText);
          throw new Error(getApiErrorMessage(error, "Failed to update SMS marketing preference"));
        }

        throw error;
      }
    },
    onSettled: refreshExactUserData,
  } satisfies MutationOptions<ModelsSmsMarketingConsentResponse, Error, ModelsUpdateSmsMarketingConsentRequest>;
}

export async function executeMeMutation(data: UpdateMeOptions, options?: UpdateMeMutationOptions) {
  return executeMutation(queryClient, createUpdateMeOptions(options), data);
}

export async function executeNotificationSettingsMutation(
  data: UpdateNotificationSettingsRequest,
  options?: UpdateMeMutationOptions,
) {
  return executeMutation(queryClient, createNotificationSettingsMutationOptions(options), data);
}

export async function executeSmsMarketingConsentMutation(data: ModelsUpdateSmsMarketingConsentRequest) {
  return executeMutation(queryClient, createSmsMarketingConsentMutationOptions(), data);
}

export async function invalidateUserData() {
  await queryClient.invalidateQueries({ queryKey: USER_KEY.all });
}

export function markUserAsDevoPlusInCache() {
  queryClient.setQueryData<User | null>(USER_KEY.all, (cachedUser) => {
    if (!cachedUser) return cachedUser;

    return {
      ...cachedUser,
      hasDevoPlus: true,
    };
  });
}
